/**
 * Phase 27b — In-app notification bell.
 *
 * - Polls `/api/notifications/unread-count` every 30s + on window focus.
 * - Popover lists up to 10 most recent notifications
 *   (`/api/notifications?limit=10`, unread first via backend sort).
 * - Each row: type icon + title + body + relative timestamp + mark-read affordance.
 * - Header has a "Mark all read" button when unread > 0.
 * - Empty state: friendly copy.
 * - Deep-link honored from `payload.deep_link` (Phase 27 model) — falls back
 *   to no-op if absent. For `schedule_published` notifications, defaults to
 *   `/app/time/week?period_start_local=<week>`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Bell, CalendarClock, Coffee, PalmtreeIcon, Info, CheckCheck, Loader2 } from "lucide-react";

const TYPE_META = {
  schedule_published: { icon: CalendarClock, tone: "text-primary" },
  pto_decided:        { icon: PalmtreeIcon,  tone: "text-secondary" },
  break_violation_flag: { icon: Coffee,      tone: "text-amber-600" },
  generic:            { icon: Info,          tone: "text-muted-foreground" },
};

function relTime(iso) {
  if (!iso) return "";
  const now = Date.now();
  const then = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function resolveDeepLink(n) {
  if (n?.deep_link) return n.deep_link;
  if (n?.payload?.deep_link_url) return n.payload.deep_link_url;
  if (n?.type === "schedule_published" && n?.payload?.week_start_local) {
    return `/app/time/week?period_start_local=${n.payload.week_start_local}`;
  }
  return null;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 10 } });
      setItems(data.items || []);
      setUnread(data.unread ?? 0);
    } catch (_e) {
      // Best-effort — bell should never break the page.
    }
  }, []);

  const refreshCountOnly = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread ?? 0);
    } catch (_e) {}
  }, []);

  useEffect(() => {
    refreshCountOnly();
    const onFocus = () => refreshCountOnly();
    window.addEventListener("focus", onFocus);
    pollRef.current = setInterval(refreshCountOnly, 30 * 1000);
    return () => {
      window.removeEventListener("focus", onFocus);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [refreshCountOnly]);

  useEffect(() => {
    if (open) {
      setLoading(true);
      refresh().finally(() => setLoading(false));
    }
  }, [open, refresh]);

  const markOne = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
      setUnread((n) => Math.max(0, n - 1));
    } catch (_e) {}
  };

  const markAll = async () => {
    setMarkingAll(true);
    try {
      await api.post("/notifications/read-all");
      const now = new Date().toISOString();
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || now })));
      setUnread(0);
    } catch (_e) {}
    setMarkingAll(false);
  };

  const openItem = async (n) => {
    if (!n.read_at) await markOne(n.id);
    const url = resolveDeepLink(n);
    setOpen(false);
    if (url) navigate(url);
  };

  const cap = unread > 99 ? "99+" : String(unread);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={
            unread > 0 ? `Notifications, ${unread} unread` : "Notifications"
          }
          className="relative grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition hover:bg-accent focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="notification-bell"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-white"
              data-testid="notification-bell-count"
              aria-hidden="true"
            >
              {cap}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-96 p-0"
        data-testid="notification-bell-panel"
      >
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
            Notifications
          </div>
          {unread > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={markAll}
              disabled={markingAll}
              className="h-7 gap-1 text-xs"
              data-testid="notification-mark-all-read"
            >
              {markingAll ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <CheckCheck className="h-3 w-3" />
              )}
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {loading && items.length === 0 && (
            <div className="flex items-center gap-2 px-3 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div
              className="px-3 py-8 text-center text-sm text-muted-foreground"
              data-testid="notification-empty"
            >
              You're clear — no new notifications.
            </div>
          )}
          {items.map((n) => {
            const meta = TYPE_META[n.type] || TYPE_META.generic;
            const Icon = meta.icon;
            const isUnread = !n.read_at;
            return (
              <div
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                data-unread={isUnread ? "true" : "false"}
                className={`border-b border-border/50 last:border-b-0 ${
                  isUnread ? "bg-accent/30" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() => openItem(n)}
                  className="w-full px-3 py-3 text-left transition hover:bg-accent/60 focus:bg-accent/60 focus:outline-none"
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 ${meta.tone}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="truncate text-sm font-medium text-primary">
                          {n.title}
                        </div>
                        {isUnread && (
                          <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
                        )}
                      </div>
                      {n.body && (
                        <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                          {n.body}
                        </div>
                      )}
                      <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                        {relTime(n.created_at)}
                      </div>
                    </div>
                    {isUnread && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          markOne(n.id);
                        }}
                        className="text-[10px] font-medium uppercase tracking-wider text-primary hover:underline"
                        data-testid={`notification-mark-read-${n.id}`}
                        aria-label={`Mark ${n.title} as read`}
                      >
                        Mark read
                      </button>
                    )}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
